/**
 * The Phase 2 `MessageId` set — the en/ar/hi catalogs cover exactly this.
 *
 * Two sources make up everything the Phase 2 UI emits:
 *
 *  1. STRUCTURAL keys introduced by the L4 builder. These MIRROR
 *     `packages/view/src/messages.ts` (Agent 5's `MSG` / `VIEW_MESSAGE_KEYS`) **verbatim**.
 *     They are duplicated here (rather than imported) so `@progression/i18n` stays runnable
 *     and testable alone — the brief says build/test against this package + a sample
 *     view-model, and reference neighbours only in the report. The correspondence is checked
 *     by reading view/messages.ts; if Agent 5 extends the set, mirror the additions here.
 *
 *  2. DOMAIN-OBJECT keys carried on the entities themselves (`Claim.labelId`,
 *     `Edge.sourceLabelId`, constraint labels), taken from the §9.1 shared fixture
 *     (`@progression/contracts/fixtures`). The view-model emits these too, so the catalogs
 *     must cover them.
 *
 * INV-STABLE-ID: every one of these is a stable id; the catalogs map them to strings and
 * nothing else. Swapping the catalog (en→ar→hi, or a mistranslation) cannot move a verdict.
 */
import { messageId } from '@progression/contracts';
/**
 * STRUCTURAL keys — a verbatim mirror of `view/src/messages.ts`. Param expectations noted.
 */
export const VIEW_MSG = {
    // layout / sections
    inquiryTitle: 'view.inquiry.title',
    sectionClaims: 'view.section.claims',
    sectionIntegrity: 'view.section.integrity',
    // the three axes (full register)
    axisWarrantLabel: 'axis.warrant.label',
    axisWarrantValue: 'axis.warrant.value', // params: { w: number }
    axisEmpiricalLabel: 'axis.empirical.label',
    axisEmpiricalValue: 'axis.empirical.value', // params: { kind: string } — ICU select
    axisModalLabel: 'axis.modal.label',
    axisModalValue: 'axis.modal.value', // params: { m: number }
    // coarse single axis line for the kid register
    axisKidSummary: 'axis.kid.summary', // params: { w: number }
    // presentational verdict states
    stateExplorable: 'state.explorable',
    stateRefuted: 'state.refuted',
    stateOutside: 'state.outside',
    // verdict detail
    verdictConflict: 'verdict.conflict', // params: { constraints: string }
    verdictOutside: 'verdict.outside', // params: { missing: string }
    // precision-loss notes
    lossRegisterPlain: 'loss.register.plain',
    lossRegisterKid: 'loss.register.kid',
    lossProfileDetail: 'loss.profile-detail',
    lossConflictDetail: 'loss.conflict-detail',
    // integrity lanes (Machine-track values)
    laneVerified: 'lane.verified',
    laneUnverified: 'lane.unverified',
    laneConflict: 'lane.conflict',
    // --- Phase 2 view-chrome additions (Agent-5-owned; DECISIONS_Agent5_Phase2 P2-C1) ---
    // additional sections
    sectionConstraints: 'view.section.constraints',
    sectionEdges: 'view.section.edges',
    sectionFrame: 'view.section.frame',
    sectionExposure: 'view.section.exposure',
    // constraint statuses (the no-ambiguity calculus)
    statusAdoptedFormal: 'status.adopted-formal',
    statusAdoptedInterpretive: 'status.adopted-interpretive',
    statusSuspended: 'status.suspended',
    // typed-edge effects on the config
    edgeEffectConstraint: 'edge.effect.constraint',
    edgeEffectResource: 'edge.effect.resource',
    edgeEffectHeuristic: 'edge.effect.heuristic',
    edgeEffectShared: 'edge.effect.shared-foundation',
    // framing (a frame is a config)
    frameActive: 'view.frame.active', // params: { count: number } — ICU plural
    // per-claim exposure (adopted-interpretive dependencies)
    exposureNone: 'view.exposure.none',
    exposureDep: 'view.exposure.dep', // params: { id: string }
    // the three integrity-lane TRACK labels
    laneTrackMachine: 'lane.machine',
    laneTrackExpert: 'lane.expert',
    laneTrackCommunity: 'lane.community',
    // Expert lane values
    expertAudited: 'lane.expert.audited',
    expertInProgress: 'lane.expert.in-progress',
    expertNotReviewed: 'lane.expert.not-reviewed',
    // Community lane values
    communityFlags: 'lane.community.flags',
    communityNone: 'lane.community.none',
    // interactive control labels
    controlChallenge: 'control.challenge',
    controlActivate: 'control.activate',
    controlMakeInterpretive: 'control.make-interpretive',
    controlImportEdge: 'control.import-edge',
    controlPromote: 'control.promote',
    controlUndo: 'control.undo',
    controlRedo: 'control.redo',
    controlRegister: 'control.register',
    // promotion outcome chrome
    promoteRejected: 'view.promote.rejected',
    promoteAccepted: 'view.promote.accepted',
    // keystone-guard reason ids (mirrored from the kernel; resolve the same)
    promoteReasonRefutationTerminal: 'promote.proof-cannot-cure-refutation',
    promoteReasonKeystone: 'promote.keystone-needs-empirical',
    promoteReasonEmpiricalNotFixed: 'promote.empirical-not-fixed',
    promoteReasonModalNotFixed: 'promote.modal-not-fixed',
    promoteReasonWarrantNotImproved: 'promote.warrant-not-improved',
};
/**
 * DOMAIN-OBJECT keys from the §9.1 fixture (carried on the entities). Mirrors the labelIds
 * declared in `@progression/contracts/fixtures`.
 */
export const DOMAIN_MSG = {
    // constraint labels
    constraintLogic: 'constraint.logic',
    constraintLightBends: 'constraint.light-bends',
    constraintParallelPostulate: 'constraint.parallel-postulate',
    // claim labels
    claimScalarGravity: 'claim.scalar-gravity',
    claimNonEuclidean: 'claim.non-euclidean',
    claimSubstrateSr: 'claim.substrate-sr',
    // edge source labels
    edgeExtObservation: 'edge.ext-observation',
    edgeResourceSample: 'edge.resource-sample',
    edgeHeuristicSample: 'edge.heuristic-sample',
    edgeSharedSample: 'edge.shared-sample',
};
/**
 * Compute-integration keys (Agent 6 ↔ Agent 8/Agent 5). The L4 view-model emits these when a
 * computation result is attached to a claim. The numeric `{result}` is pre-formatted at the
 * L5 render boundary by `formatResult(value, locale)` (locale decimal/grouping/digit shaping),
 * so the catalog string is locale-faithful connective tissue, not a number formatter.
 */
export const COMPUTE_MSG = {
    /** params: { engine, op, result, config } — all strings (`result` pre-formatted). */
    evidenceComputation: 'evidence.computation',
    /** param: { dp } — decimal-places count (a plain number, not a plural agreement). */
    lossNumericRounding: 'loss.numeric-rounding',
};
/** The complete vocabulary (structural ∪ domain ∪ compute), ergonomic grouped form. */
export const P2 = { ...VIEW_MSG, ...DOMAIN_MSG, ...COMPUTE_MSG };
/** Every Phase 2 `MessageId` (raw string), de-duplicated + sorted. Catalogs cover exactly this. */
export const PHASE2_MESSAGE_IDS = Object.freeze(Array.from(new Set(Object.values(P2))).sort());
export const PHASE2_MESSAGE_ID_SET = new Set(PHASE2_MESSAGE_IDS);
/** Brand a Phase 2 id string as a `MessageId`. */
export const P2_ID = (id) => messageId(id);
//# sourceMappingURL=message-ids-phase2.js.map